import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Switch,
    Typography,
    Card,
    CardContent,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    Container,
    Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import api from "core/interceptors/api";

const permissionGroups = {
    dashboard: [],
    vendor_management: [
        "vendor_register",
        "vendor_approve",
        "vendor_inactive",
        "vendor_products",
        "vendor_categories",
        "vendor_orders",
        "business_category",
    ],
    service_management: [
        "service_register",
        "service_approve",
        "service_type",
        "service_specialization",
    ],
    report_analytics: ["admin_sales_report"],
    settings: ["banner"],
};


const TelecallerControls = () => {
    const navigate = useNavigate();
    const [telecallers, setTelecallers] = useState([]);
    const [selectedTelecaller, setSelectedTelecaller] = useState("AllTelecallers");
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(false);
    const [changesMade, setChangesMade] = useState(false);

    useEffect(() => {
        const fetchTelecallers = async () => {
            try {
                const res = await api.get("/login/all");
                setTelecallers((res.data || []).filter((user) => user.role === "Telecaller"));
            } catch (error) {
                console.error("Error fetching users:", error);
            }
        };
        fetchTelecallers();
    }, []);

    useEffect(() => {
        const fetchPermissions = async () => {
            if (selectedTelecaller === "AllTelecallers") {
                setPermissions({});
                setChangesMade(false);
                return;
            }

            setLoading(true);
            try {
                const res = await api.get(`/telecaller/get_permissions?email=${selectedTelecaller}`);
                const fetchedPermissions = res.data.permissions || {};
                delete fetchedPermissions.email;
                setPermissions(fetchedPermissions);
                setChangesMade(false);
            } catch (error) {
                console.error("Error fetching permissions:", error);
                setPermissions({});
                Swal.fire({
                    title: "Error",
                    text: "Could not fetch permissions.",
                    icon: "error",
                    timer: 2000,
                    showConfirmButton: false,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchPermissions();
    }, [selectedTelecaller]);

    const handleTelecallerSelect = (event) => {
        setSelectedTelecaller(event.target.value);
    };

    const handleToggle = (field) => {
        setPermissions((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
        setChangesMade(true);
    };

    const handleSave = async () => {
        if (!changesMade) return;
        try {
            await api.post("telecaller/update", {
                email: selectedTelecaller,
                ...permissions,
            });
            setChangesMade(false);
            Swal.fire({
                title: "Saved!",
                text: "Permissions updated successfully.",
                icon: "success",
                timer: 1200,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error("Error updating permissions:", error);
            Swal.fire({
                title: "Error",
                text: "Failed to update permissions. Please try again.",
                icon: "error",
                timer: 2000,
                showConfirmButton: false,
            });
        }
    };

    return (
        <DashboardLayout>
            <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                <LogoutButton onLogout={() => navigate("/login")} />
            </MDBox>
            <DashboardNavbar />

            <Container maxWidth="md" sx={{ mt: 3, mb: 10 }}>
                <Paper elevation={3} sx={{ p: 3, bgcolor: "#f3f9f7", borderRadius: 2 }}>
                    <Box mb={2}>
                        <FormControl variant="outlined" size="medium" sx={{ height: "45px", width: "250px" }}>
                            <InputLabel sx={{ fontSize: "0.50rem" }}>Select Telecaller</InputLabel>
                            <Select
                                value={selectedTelecaller}
                                onChange={handleTelecallerSelect}
                                IconComponent={ExpandMoreIcon}
                                label="Select Telecaller"
                                sx={{ fontSize: "0.75rem", height: 36 }}
                            >
                                <MenuItem value="AllTelecallers">All Telecallers</MenuItem>
                                {telecallers.map((tc) => (
                                    <MenuItem key={tc.id} value={tc.email}>
                                        {tc.email}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {selectedTelecaller !== "AllTelecallers" && (
                        <>
                            {loading ? (
                                <Box textAlign="center" my={2}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : (
                                Object.entries(permissionGroups).map(([groupKey, fields]) => {
                                    if (fields.length === 0 && groupKey !== "dashboard") return null;
                                    const displayFields = groupKey === "dashboard" ? ["dashboard"] : fields;

                                    return (
                                        <Box key={groupKey} mb={2}>
                                            <Typography
                                                variant="caption"
                                                fontWeight="bold"
                                                color="text.secondary"
                                                sx={{ mb: 1, display: "block" }}
                                            >
                                                {groupKey.replace(/_/g, " ").toUpperCase()}
                                            </Typography>
                                            <Grid container spacing={1}>
                                                {displayFields.map((field) => (
                                                    <Grid item xs={12} sm={6} md={4} key={field}>
                                                        <Card
                                                            sx={{
                                                                px: 1.5,
                                                                py: 0.5,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "space-between",
                                                                borderRadius: 1.5,
                                                                boxShadow: 1,
                                                                background: "#ffffff",
                                                                minHeight: 50,
                                                            }}
                                                        >
                                                            <CardContent
                                                                sx={{
                                                                    p: 4,
                                                                    flexGrow: 0,
                                                                    flexShrink: 1,
                                                                    flexBasis: "70%",
                                                                    maxWidth: "220px",
                                                                    overflow: "hidden",
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontSize: "0.7rem",
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                    }}
                                                                    fontWeight="medium"
                                                                >
                                                                    {field.replace(/_/g, " ").toUpperCase()}
                                                                </Typography>
                                                            </CardContent>
                                                            <Switch
                                                                checked={Boolean(permissions[field])}
                                                                onChange={() => handleToggle(field)}
                                                                size="small"
                                                                sx={{
                                                                    transform: "scale(0.8)",
                                                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                                                        color: "#00C9A7",
                                                                    },
                                                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                                                        backgroundColor: "#00C9A7",
                                                                    },
                                                                }}
                                                            />
                                                        </Card>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>
                                    );
                                })
                            )}
                        </>
                    )}
                </Paper>
            </Container>

            {selectedTelecaller !== "AllTelecallers" && (
                <Box
                    sx={{
                        position: "sticky",
                        bottom: 0,
                        width: "100%",
                        backgroundColor: "#fff",
                        borderTop: "1px solid #eee",
                        py: 1.5,
                        display: "flex",
                        justifyContent: "center",
                        zIndex: 999,
                        mt: 3,
                        borderRadius: 2,
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!changesMade}
                        sx={{
                            px: 3,
                            py: 0.8,
                            fontSize: "0.75rem",
                            borderRadius: 2,
                            backgroundColor: changesMade ? "#1976d2" : "#ccc",
                            color: "white !important",
                            opacity: changesMade ? 1 : 0.7,
                            boxShadow: 1,
                            "&:hover": {
                                backgroundColor: changesMade ? "#1565c0" : "#ccc",
                            },
                        }}
                    >
                        SAVE
                    </Button>
                </Box>
            )}
        </DashboardLayout>
    );
};

export default TelecallerControls;
